import * as yup from 'yup';

import { countPhoneDigits } from '@/utils/helper';

import {
  EMAIL_PATTERN,
  HEIGHT_INCHES_PATTERN,
  MAX_AVATAR_FILE_SIZE,
  MAX_CITY_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_HEIGHT_LENGTH,
  MAX_NAME_LENGTH,
  MAX_STREET_ADDRESS_LENGTH,
  MAX_ZIP_LENGTH,
  PHONE_MAX_DIGITS,
  PHONE_MIN_DIGITS,
} from './constants';

/**
 * Reusable yup field rules shared by the guardian / player / coach forms.
 *
 * Each builder takes the validation messages it needs (the screens live under
 * different `STRINGS.*.validation` roots) and returns a base schema. Callers can
 * chain `.defined()` / `.optional()` / `.nullable()` to match each form's type.
 */

/** Required name (first / last / parent): trimmed, ≤ {@link MAX_NAME_LENGTH}. */
export const nameRule = (requiredMessage: string) =>
  yup.string().trim().required(requiredMessage).max(MAX_NAME_LENGTH);

/** Required email: trimmed, ≤ {@link MAX_EMAIL_LENGTH}, valid format. */
export const emailRule = (requiredMessage: string, invalidMessage: string) =>
  yup
    .string()
    .trim()
    .required(requiredMessage)
    .max(MAX_EMAIL_LENGTH)
    .matches(EMAIL_PATTERN, invalidMessage)
    .email(invalidMessage);

/** Required phone: present, with {@link PHONE_MIN_DIGITS}–{@link PHONE_MAX_DIGITS} digits. */
export const requiredPhoneRule = (requiredMessage: string, digitsRangeMessage: string) =>
  yup
    .string()
    .required(requiredMessage)
    .test('digits-range', digitsRangeMessage, value => {
      const digits = countPhoneDigits(value);
      return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
    });

/**
 * Optional phone: empty is allowed; otherwise {@link PHONE_MIN_DIGITS}–{@link PHONE_MAX_DIGITS}
 * digits. Used for the alternate phone and the coach's optional phone (chain
 * `.defined()` or `.optional().nullable()` as the form type requires).
 */
export const optionalPhoneRule = (digitsRangeMessage: string) =>
  yup.string().test('digits-range', digitsRangeMessage, value => {
    const digits = countPhoneDigits(value);
    if (digits === 0) return true;
    return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
  });

/** Optional city: trimmed, defined, ≤ {@link MAX_CITY_LENGTH}. */
export const cityRule = () => yup.string().trim().defined().max(MAX_CITY_LENGTH);

/** Optional street address line: trimmed, defined, ≤ {@link MAX_STREET_ADDRESS_LENGTH}. */
export const streetAddressRule = () => yup.string().trim().defined().max(MAX_STREET_ADDRESS_LENGTH);

/** Optional zip / postal code: trimmed, defined, ≤ {@link MAX_ZIP_LENGTH}. */
export const zipCodeRule = () => yup.string().trim().defined().max(MAX_ZIP_LENGTH);

/**
 * Avatar / photo: only the file size is validated (capped at
 * {@link MAX_AVATAR_FILE_SIZE}); the uri is optional.
 */
export const avatarRule = (requiredMessage: string, tooLargeMessage: string) =>
  yup
    .object({
      uri: yup.string().required(requiredMessage).optional().nullable(),
      fileSize: yup.number().min(1).max(MAX_AVATAR_FILE_SIZE, tooLargeMessage).optional(),
    })
    .optional()
    .nullable();

/**
 * Player height: optional, 1–2 digits with an optional inch mark (`48"`),
 * ≤ {@link MAX_HEIGHT_LENGTH} characters.
 */
export const playerHeightRule = (invalidMessage: string) =>
  yup
    .string()
    .defined()
    .test('height-inches', invalidMessage, value => !value || HEIGHT_INCHES_PATTERN.test(value))
    .max(MAX_HEIGHT_LENGTH);

/**
 * Required birth date: must be a Date and not in the future (compared by
 * calendar day). Pass `todayStart` (midnight) to pin "today", or omit to compute
 * it at validation time.
 */
export const birthDateRule = (
  requiredMessage: string,
  notFutureMessage: string,
  todayStart?: Date,
) =>
  yup
    .date()
    .nullable()
    .defined()
    .test('birthDate-required', requiredMessage, value => value instanceof Date)
    .test('birthDate-not-future', notFutureMessage, value => {
      if (!(value instanceof Date)) return false;
      const selected = new Date(value);
      selected.setHours(0, 0, 0, 0);
      let today = todayStart;
      if (!today) {
        today = new Date();
        today.setHours(0, 0, 0, 0);
      }
      return selected.getTime() <= today.getTime();
    });
